import axiosInstance from './axiosInstance'
import { authorizedGraphQLRequest } from './graphqlClient'
import { gql } from 'graphql-request'

export interface ChatRequest {
  message: string
  fake?: boolean
  chat_mode?: 'continue' | 'new'
  folder?: string
  language?: string
}

export interface ChatResponse {
  content?: string
  response?: string
  exit: boolean
  role_play_game?: boolean
  system_prompt?: string[]
}

export interface CaseUpdateRequest {
  case_number: number
}

export interface CaseUpdateResponse {
  success: boolean
  message: string
}

export interface ChatFoldersResponse {
  folders: string[]
}

export const chatApi = {
  // Send a message to the chatbot
  sendMessage: async (data: ChatRequest): Promise<ChatResponse> => {
    const response = await axiosInstance.post<ChatResponse>('/chat', data, {
      headers: {
        Authorization: 'Bearer ' + localStorage.getItem('auth_token'),
      },
    })
    return response.data
  },

  // Send a message to the role play chatbot
  sendRolePlayMessage: async (data: ChatRequest): Promise<ChatResponse> => {
    const response = await axiosInstance.post<ChatResponse>('/chat_role_play', data, {
      headers: {
        Authorization: 'Bearer ' + localStorage.getItem('auth_token'),
      },
    })
    return response.data
  },

  // Update the selected case
  updateChatbot: async (): Promise<CaseUpdateResponse> => {
    const response = await axiosInstance.get<CaseUpdateResponse>('/update_chatbot', { timeout: 180000 })
    return response.data
  },

  // Update the role play chatbot
  updateRolePlayChatbot: async (): Promise<CaseUpdateResponse> => {
    const response = await axiosInstance.get<CaseUpdateResponse>('/update_role_play_chatbot', { timeout: 180000 })
    return response.data
  },

  // Get available chat folders
  getChatFolders: async (): Promise<ChatFoldersResponse> => {
    const response = await axiosInstance.post<ChatFoldersResponse>('/get_chat_folders')
    return response.data
  },

  // Get Heygen token using GraphQL
  getHeygenToken: async (): Promise<{ token: string }> => {
    const query = gql`
      query GetHeygenToken {
        getHeygenToken {
          token
        }
      }
    `

    const data = await authorizedGraphQLRequest<{
      getHeygenToken: { token: string }
    }>(query)
    return data.getHeygenToken
  },

  // Get LiveAvatar session token using GraphQL
  // `meteredSessionId`: B2C users are refused a token without their open roleplay session.
  getLiveAvatarToken: async (config?: {
    avatarId?: string
    mode?: 'CUSTOM' | 'FULL' | 'LITE'
    instructions?: string
    meteredSessionId?: number | null
  }): Promise<{ sessionToken: string; sessionId: string }> => {
    const query = gql`
      query GetLiveAvatarToken($avatarId: String, $mode: String, $instructions: String, $meteredSessionId: Int) {
        getLiveAvatarToken(avatarId: $avatarId, mode: $mode, instructions: $instructions, meteredSessionId: $meteredSessionId) {
          sessionToken
          sessionId
        }
      }
    `

    const data = await authorizedGraphQLRequest<{
      getLiveAvatarToken: { sessionToken: string; sessionId: string }
    }>(query, {
      avatarId: config?.avatarId,
      mode: config?.mode || 'CUSTOM',
      instructions: config?.instructions,
      meteredSessionId: config?.meteredSessionId ?? null,
    })
    return data.getLiveAvatarToken
  },

  // Get payloads from microfrank
  getPayloads: async (projectId: number, page?: number, limit?: number) => {
    const query = gql`
      query GetPayloads($projectId: Int!, $page: Int, $limit: Int) {
        getPayloads(projectId: $projectId, page: $page, limit: $limit) {
          data {
            id
            name
            description
            projectId
            content
          }
          pagination {
            page
            limit
            total
            totalPages
          }
        }
      }
    `

    const data = await authorizedGraphQLRequest<{
      getPayloads: {
        data: Array<{
          id: number
          name: string
          description?: string
          projectId: number
          content: string
        }>
        pagination: {
          page: number
          limit: number
          total: number
          totalPages: number
        }
      }
    }>(query, { projectId, page, limit })
    return data.getPayloads
  },
}
